import React from "react"
import { useBlocks } from "app/regions/hooks/use-blocks"
import { templates } from "./templates"
import { getTemplate } from "./utils"
import { OldButton } from "@kit/design/old-button"

// Values retrieved from local storage are always strings. For instance, `false`
// actually becomes the string "false", which is truthy. This function converts
// strings back to their expected types.
export function switchToType(value) {
  if (value === "null") return null

  if (!isNaN(value)) return parseInt(value)

  return value
}

// This function is a nice-to-have for UX. When a user adds a new layout block,
// we'll merge its default settings with their last-added ones so they don't
// need to manually set the colors/padding/etc. to match all over again.
export function mergeLocalLayoutSettings(name, properties) {
  const result = {}

  for (const propertyName in properties) {
    const preferred = localStorage.getItem(`layout-${name}-${propertyName}`)
    const defaultSetting = properties[propertyName]

    const preferredTyped = switchToType(preferred)

    if (preferredTyped || preferredTyped === null) {
      result[propertyName] = preferredTyped
    } else {
      result[propertyName] = defaultSetting
    }
  }

  return result
}

// This component displays a list of each layout template. Clicking a template
// will switch the layout block to use that template.
function TemplateSelector({ block_id }) {
  const { add, replaceProperties } = useBlocks(block_id)

  function selectTemplate(name) {
    const template = getTemplate(name)

    replaceProperties({
      template: name,
      ...mergeLocalLayoutSettings(name, template.defaultProperties)
    })

    add(template.defaultChildren, { focus: false })
  }

  return (
    <div>
      {templates.map(template => {
        return (
          <button
            key={template.name}
            onClick={() => selectTemplate(template.name)}
            className="relative border rounded mb-4 block overflow-hidden hover:opacity-75 transition-opacity duration-200"
          >
            <img
              src={template.image}
              alt={template.label}
              title={template.label}
            />
            {template.beta && (
              <div className="absolute top-0 right-0 m-1 px-1 rounded bg-red-500 text-white text-uppercase font-semibold">
                Beta
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

// This is the main settings component for layout blocks. If no template is
// selected, it will render the template menu; otherwise, it will render the
// settings for that particular template (which are defined in ./templates.js.)
export function LayoutSettings({ block_id, properties }) {
  const { remove } = useBlocks(block_id)
  const SettingsComponent = getTemplate(properties.template)?.SettingsComponent

  return (
    <div className="px-4 pb-4">
      {!properties.template ? (
        <TemplateSelector block_id={block_id} />
      ) : (
        <SettingsComponent block_id={block_id} />
      )}
      <OldButton
        size="sm"
        is="secondary"
        className="mt-4 ml-auto block"
        onClick={() => remove(block_id)}
        icon="delete"
      >
        Remove layout
      </OldButton>
    </div>
  )
}
